/**
 * README
 * This is an "internal" component meant to be used solely by other RC components.
 * Please don't use this directly. If it seems like you'd benefit from it please
 * reach out to #cadence for help coordinating!
 */
import React, { cloneElement, useLayoutEffect } from 'react';
import classNames from 'classnames';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  safePolygon,
  useDelayGroup,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useClick,
  limitShift,
  useHover,
  useTransitionStatus,
  Placement,
} from '@floating-ui/react';
import mergeRefs from 'react-merge-refs';
import { FLOATING_UI_DOM_ID, PANEL_DEFAULTS } from '@reverbdotcom/cadence/constants';

/**
 * Lets content rendered inside the panel close the surface containing it.
 *
 * Internal — consumers reach this through `useRCPopover`, which is what RCPopover exports and the
 * only name they should need to know.
 *
 * Defaults to a no-op rather than `null` so a component reused both inside and outside a popover
 * doesn't crash on the outside path.
 */
export interface PopoverContextValue {
  close: () => void;
}

const RCNonModalDialogBaseContext = React.createContext<PopoverContextValue>({ close: () => {} });

export const useRCNonModalDialogBaseContext = () => React.useContext(RCNonModalDialogBaseContext);

/**
 * How a popover-like surface finds its reference element — provide EXACTLY ONE:
 * - `anchor`: an element the surface clones + owns (gets the ref + interaction props), or
 * - `anchorRef`: a ref to an element the consumer already renders (positioning-only, nothing is
 *   cloned, no interaction props) so one trigger can be shared by two overlays (a menu + guide
 *   popover).
 * The discriminated union makes passing both — or neither — a compile error. Shared across the
 * popover wrappers (RCGuidePopover, etc.), which forward it by branching on the intact union so TS
 * narrows the sibling — no cast needed.
 */
export type PopoverAnchoring =
  | { anchor: JSX.Element; anchorRef?: never }
  | { anchor?: never; anchorRef: React.RefObject<HTMLElement> };

type RCNonModalDialogBaseProps = {
  id: string,
  isOpen?: boolean,
  onOpenChange?: (open: boolean) => void,
  sticky?: boolean,
  position?: Placement,
  children: React.ReactNode,
  /** Overrides base functionality to suit specific purposes
   * - Guide Popover: Adds arrow and delays animate in
   * - Legacy: temporary variant that does not automatically add a click handler to support legacy instances
   */
  variant?: 'default' | 'guide-popover' | 'legacy';
  dismissOnOutsidePress?: boolean;
  focusOrder?: Array<'reference' | 'floating' | 'content'>;
  trapFocus?: boolean;
  /** Index of the focusOrder array that should receive initial focus. Defaults to 0. Use -1 to prevent initial focus. */
  initialFocus?: -1 | 0 | 1 | 2;
  openOnHover?: boolean;
  /** Id of the element that names the panel, for a panel labelled by something outside it (e.g. its
   * trigger). Defaults to `${id}-label`, an element the panel's content is expected to render. */
  labelledBy?: string;
  /** Id of the element that describes the panel. Defaults to `${id}-description`, an element the
   * panel's content is expected to render. */
  describedBy?: string;
} & PopoverAnchoring;

function ariaID(id) {
  return {
    label: `${id}-label`,
    description: `${id}-description`,
  };
}

function arrowSide(panelPlacement) {
  // the side of the anchor that the panel sits on comes from `placement`
  const [ panelSide ] = panelPlacement.split('-');

  // the arrow sits on that opposite side, so we remap it here.
  const arrowSideMapping = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  return arrowSideMapping[panelSide];
}

function arrowPositionStyles(arrowData, panelPlacement) {
  return {
    top: arrowData?.y && `${arrowData?.y}px`,
    left: `${arrowData?.x}px`,

    // override defaults above based on position
    [arrowSide(panelPlacement)]: `-${PANEL_DEFAULTS.arrowHeight}px`,
  };
}

export function RCNonModalDialogBase({
  id,
  isOpen: openProp = undefined,
  onOpenChange: setOpenProp = undefined,
  sticky = false,
  position = 'bottom-start',
  anchor = undefined,
  anchorRef = undefined,
  children,
  variant = 'default',
  dismissOnOutsidePress = true,
  focusOrder = ['content'],
  trapFocus = true,
  initialFocus = 0,
  openOnHover = false,
  labelledBy = undefined,
  describedBy = undefined,
}: RCNonModalDialogBaseProps) {
  const arrowRef = React.useRef(null);
  const labelId = labelledBy ?? ariaID(id).label;
  const descriptionId = describedBy ?? ariaID(id).description;

  const [openState, setOpenState] = React.useState<boolean>(openProp || false);

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? setOpenProp : setOpenState;

  const handleOpenChange = React.useCallback((state: boolean) => {
    setOpen(state);
    if (!isControlled) {
      setOpenProp?.(state);
    }
  }, [setOpen, isControlled, setOpenProp]);

  // Built on `handleOpenChange`, not the raw `setOpen`: closing from inside the panel has to run
  // the same path as an outside-press or Escape, or an uncontrolled popover would close without
  // ever firing the consumer's `onOpenChange`.
  const contextValue = React.useMemo(
    () => ({ close: () => handleOpenChange(false) }),
    [handleOpenChange],
  );

  const isGuidePopover = variant === 'guide-popover';

  // Main config for libs "useFloating"
  const {
    x,
    y,
    refs,
    strategy,
    context,
    update,
    placement,
    middlewareData,
  } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    strategy: 'fixed',
    middleware: [
      offset(PANEL_DEFAULTS.anchorOffset),
      flip(),
      shift({
        padding: {
          top: PANEL_DEFAULTS.arrowHeight,
          right: PANEL_DEFAULTS.windowEdgePadding,
          bottom: PANEL_DEFAULTS.windowEdgePadding,
          left: PANEL_DEFAULTS.windowEdgePadding,
        },
        mainAxis: true,
        crossAxis: true,
        limiter: sticky ? undefined : limitShift(),
      }),
      size({
        apply({ availableHeight, elements }) {
          const safeValue = Math.max(0, availableHeight);
          elements.floating.style.setProperty(
            '--available-height',
            `${safeValue / 10}rem`,
          );
        },
      }),
      arrow({
        element: arrowRef,
        padding: 16, // equal to border radius of popover panel
      }),
    ],
    placement: position,
    whileElementsMounted: (r, f, u) => {
      // We only need to auto update if it's possible to do so
      let cleanup = undefined;
      if (('ResizeObserver' in window)) {
        // animationFrame: true ensures the position stays correct when layout
        // shifts occur after initial render (e.g. ads or async content loading
        // above the reference element).
        cleanup = autoUpdate(r, f, u, { animationFrame: true });
      }
      return cleanup;
    },
  });

  // Prefer parent DelayGroup's delay values if available
  const { delay, currentId, isInstantPhase } = useDelayGroup(context);
  const transitionDelay = delay || {
    open: 350,
    close: 100,
  };

  const { isMounted, status } = useTransitionStatus(context, {
    duration: isInstantPhase ? {
      open: 0,
      close: currentId === context.floatingId ? 100 : 0,
    } : transitionDelay,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, {
      enabled: isGuidePopover ? false : true,
      role: 'dialog', // this should be changed when this does more than a popover-like thing.
    }),
    useClick(context, {
      enabled: variant === 'default' ? true : false,
      toggle: true,
    }),
    useHover(context, {
      enabled: openOnHover,
      delay: transitionDelay,
      mouseOnly: true,
      // safePolygon uses `performance.now()` – only use it if available on the window
      handleClose: (typeof window !== 'undefined' && 'performance' in window) ? safePolygon() : undefined,
    }),
    useDismiss(context, {
      enabled: true,
      escapeKey: true,
      outsidePressEvent: 'mousedown',
      outsidePress: dismissOnOutsidePress,
    }),
  ]);

  // Preserve the consumer's ref
  const anchorElementRef = mergeRefs([refs.setReference, (anchor as any)?.ref]);

  // `anchorRef` mode: the consumer renders + owns the trigger elsewhere, so hand floating-ui the
  // live element (no clone, no interaction props on the trigger). A layout effect runs after refs
  // commit (so `.current` is populated) and before paint, so the panel never flashes at (0,0).
  // Intentionally no dependency array: refs are mutable, so we re-read `.current` every commit —
  // this re-points if the same RefObject is swapped to a new node. `setReference` no-ops when the
  // node is unchanged, so re-running is cheap. Skipped in clone mode, where getReferenceProps owns
  // the reference.
  useLayoutEffect(() => {
    if (anchorRef) refs.setReference(anchorRef.current ?? null);
  });

  const arrowCallback = React.useCallback(
    (node) => {
      arrowRef.current = node;
      update();
    },
    [update],
  );

  const classes = classNames(
    'rc-non-modal-dialog-base',
    { 'rc-non-modal-dialog-base--guide-popover': isGuidePopover },
  );

  if (anchor?.type === React.Fragment) {
    // Fragments will render to the screen, but they lose their ref and the Popover UIs won't know what to anchor
    // themselves to, and the panel will end up in the top-left corner of the screen.
    throw new Error('Pass a single element to the `anchor` prop, Fragments or other arrays are not supported.');
  }

  return (
    <RCNonModalDialogBaseContext.Provider value={contextValue}>
      {anchor && cloneElement(anchor, {
        ...getReferenceProps({ ref: anchorElementRef, ...anchor.props }),
      })}

      {isMounted && (
        <FloatingPortal id={FLOATING_UI_DOM_ID}>
          <FloatingFocusManager
            context={context}
            modal={trapFocus}
            order={focusOrder}
            returnFocus
            initialFocus={initialFocus}
            closeOnFocusOut={isGuidePopover ? false : true}
          >
            <div
              data-status={status}
              data-placement={placement}
              data-instant={isInstantPhase}
              ref={refs.setFloating}
              className={classes}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
              }}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              role={isGuidePopover ? 'dialog' : undefined}
              {...getFloatingProps()}
            >
              {children}
              {isGuidePopover &&
                <div
                  className={`rc-non-modal-dialog-base__arrow rc-non-modal-dialog-base__arrow--${arrowSide(placement)}`}
                  ref={arrowCallback}
                  style={arrowPositionStyles(middlewareData?.arrow, placement)}
                />
              }
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </RCNonModalDialogBaseContext.Provider>
  );
}
